import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskApi } from '../services/api';
import useTaskStore from '../store/taskStore';

export const useTasks = () => {
  const setTasks = useTaskStore((state) => state.setTasks);
  
  return useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const tasks = await taskApi.getTasks();
      setTasks(tasks);
      return tasks;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  const addTask = useTaskStore((state) => state.addTask);
  
  return useMutation({
    mutationFn: taskApi.createTask,
    onSuccess: (newTask) => {
      addTask(newTask);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  const updateTask = useTaskStore((state) => state.updateTask);
  
  return useMutation({
    mutationFn: ({ id, ...task }) => taskApi.updateTask(id, task),
    onSuccess: (updatedTask) => {
      updateTask(updatedTask.id, updatedTask);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  const deleteTask = useTaskStore((state) => state.deleteTask);
  
  return useMutation({
    mutationFn: taskApi.deleteTask,
    onSuccess: (_, id) => {
      deleteTask(id);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useMoveTask = () => {
  const queryClient = useQueryClient();
  const moveTask = useTaskStore((state) => state.moveTask);
  
  return useMutation({
    mutationFn: ({ id, column }) => taskApi.updateTaskColumn(id, column),
    onMutate: async ({ id, column }) => {
      // Optimistic update
      moveTask(id, column);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: () => {
      // Revert on error
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};
